/**
 * In-app User's Guide (/help/guide). Content lives in src/content/userGuide.ts.
 *
 * - Filters to the reader's role by default ("What I can do"), with "All roles".
 * - Search across titles, summaries, steps and tips.
 * - Deep links: /help/guide#<section-id>.
 * - "Open" buttons only appear for pages the reader can actually open.
 * - Print / Save as PDF uses the browser print dialog with a print stylesheet.
 * Standing React rule: sub-components live at module scope.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { MenuBook, OpenInNew, Print, Search, WarningAmber, TipsAndUpdates } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { canAccessRoute, isFirmAdmin } from '../../utils/permissions';
import { GUIDE, GUIDE_VERSION, GuideChapter, GuideRole, GuideSection, ROLE_LABELS } from '../../content/userGuide';

function readerRole(user: any): GuideRole {
  if (isFirmAdmin(user)) return 'admin';
  if (['lawyer', 'paralegal', 'assistant'].includes(user?.role)) return user.role;
  return 'assistant';
}

const matches = (s: GuideSection, q: string) => {
  if (!q) return true;
  const hay = [s.title, s.summary, ...(s.steps || []), ...(s.tips || []), s.caution || ''].join(' ').toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
};

interface SectionCardProps {
  section: GuideSection;
  canOpen: boolean;
  onOpen: (path: string) => void;
}

const SectionCard: React.FC<SectionCardProps> = ({ section, canOpen, onOpen }) => (
  <Card variant="outlined" id={section.id} sx={{ scrollMarginTop: 96, breakInside: 'avoid' }}>
    <CardContent>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="h6" component="h3" sx={{ mr: 1 }}>{section.title}</Typography>
        {section.isNew && <Chip size="small" color="primary" label="New" />}
        {(section.roles || []).length > 0 && section.roles!.length < 4 && section.roles!.map((r) => (
          <Chip key={r} size="small" variant="outlined" label={ROLE_LABELS[r]} />
        ))}
        <Box sx={{ flex: 1 }} />
        {section.path && canOpen && (
          <Button size="small" endIcon={<OpenInNew fontSize="small" />} onClick={() => onOpen(section.path!)} className="no-print">
            Open
          </Button>
        )}
      </Stack>
      <Typography variant="body1" sx={{ mb: section.steps?.length ? 1.5 : 0 }}>{section.summary}</Typography>
      {section.steps && section.steps.length > 0 && (
        <Box component="ol" sx={{ pl: 3, my: 0, '& li': { mb: 0.75 } }}>
          {section.steps.map((st, i) => (
            <li key={i}><Typography variant="body2">{st}</Typography></li>
          ))}
        </Box>
      )}
      {section.tips && section.tips.length > 0 && (
        <Alert icon={<TipsAndUpdates fontSize="inherit" />} severity="info" sx={{ mt: 1.5 }}>
          {section.tips.map((t, i) => (
            <Typography key={i} variant="body2" sx={{ mb: i < section.tips!.length - 1 ? 0.5 : 0 }}>{t}</Typography>
          ))}
        </Alert>
      )}
      {section.caution && (
        <Alert icon={<WarningAmber fontSize="inherit" />} severity="warning" sx={{ mt: 1.5 }}>
          <Typography variant="body2">{section.caution}</Typography>
        </Alert>
      )}
    </CardContent>
  </Card>
);

interface TocProps {
  chapters: { chapter: GuideChapter; sections: GuideSection[] }[];
  activeId: string;
  onJump: (id: string) => void;
}

const Toc: React.FC<TocProps> = ({ chapters, activeId, onJump }) => (
  <Paper variant="outlined" sx={{ position: { md: 'sticky' }, top: { md: 88 }, maxHeight: { md: 'calc(100vh - 120px)' }, overflow: 'auto' }} className="no-print">
    <List dense disablePadding>
      {chapters.map(({ chapter, sections }) => (
        <React.Fragment key={chapter.id}>
          <ListSubheader sx={{ lineHeight: '32px', fontWeight: 600 }}>{chapter.title}</ListSubheader>
          {sections.map((s) => (
            <ListItemButton key={s.id} selected={activeId === s.id} onClick={() => onJump(s.id)} sx={{ pl: 3 }}>
              <ListItemText primary={s.title} primaryTypographyProps={{ variant: 'body2' }} />
              {s.isNew && <Chip size="small" color="primary" label="New" sx={{ ml: 1, height: 18 }} />}
            </ListItemButton>
          ))}
        </React.Fragment>
      ))}
    </List>
  </Paper>
);

const UserGuidePage: React.FC = () => {
  const { user } = useAuth() as any;
  const navigate = useNavigate();
  const location = useLocation();
  const myRole = readerRole(user);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState('');

  const visible = useMemo(() => GUIDE.map((chapter) => ({
    chapter,
    sections: chapter.sections.filter((s) => (scope === 'all' || !s.roles || s.roles.includes(myRole)) && matches(s, query.trim())),
  })).filter((c) => c.sections.length > 0), [scope, query, myRole]);

  const jump = (id: string) => {
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);
  };

  // Deep link on load: /help/guide#court-forms-use
  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    const inAll = GUIDE.some((c) => c.sections.some((s) => s.id === id));
    const inMine = visible.some((c) => c.sections.some((s) => s.id === id));
    if (inAll && !inMine) setScope('all');
    const t = setTimeout(() => jump(id), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const total = visible.reduce((n, c) => n + c.sections.length, 0);

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <style>{`@media print { .no-print, nav, header, .MuiDrawer-root { display: none !important; } body { background: #fff; } }`}</style>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
        <MenuBook color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" component="h1">User&apos;s Guide</Typography>
        <Chip size="small" label={GUIDE_VERSION} variant="outlined" />
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<Print />} variant="outlined" onClick={() => window.print()} className="no-print">
          Print / Save as PDF
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        How to use ArgisLegal day to day. Showing guidance for your role ({ROLE_LABELS[myRole]}) — switch to All roles to see everything.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} className="no-print">
        <TextField
          size="small"
          placeholder="Search the guide (e.g. court forms, invoice, 2FA)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: 1, maxWidth: 560 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && setScope(v)}>
          <ToggleButton value="mine">What I can do</ToggleButton>
          <ToggleButton value="all">All roles</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {total === 0 ? (
        <Alert severity="info">
          Nothing matches “{query}”. Try another word, or switch to All roles.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Toc chapters={visible} activeId={activeId} onJump={jump} />
          </Grid>
          <Grid item xs={12} md={9}>
            <Stack spacing={4}>
              {visible.map(({ chapter, sections }) => (
                <Box key={chapter.id} component="section" aria-labelledby={`ch-${chapter.id}`}>
                  <Typography id={`ch-${chapter.id}`} variant="h5" component="h2">{chapter.title}</Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>{chapter.intro}</Typography>
                  <Stack spacing={2}>
                    {sections.map((s) => (
                      <SectionCard
                        key={s.id}
                        section={s}
                        canOpen={Boolean(s.path && canAccessRoute(user, s.path))}
                        onOpen={(path) => navigate(path)}
                      />
                    ))}
                  </Stack>
                  <Divider sx={{ mt: 3 }} />
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary">
                Something unclear or out of date? Tell us through Help &amp; Support → support request.
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default UserGuidePage;
